import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { DrawerTitle } from './ui/drawer';
import { Skeleton } from './ui/skeleton';

export default function SearchDrawer({ onClose }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  // Placeholder data for demonstration
  const placeholderSuggestions = [
    'Dainese D-Street 2 Gloves',
    'Fox Racing Dirtpaw 2.0 Gloves',
    'AGV K6 Helmet',
    'Airoh Twist 3.2 Carbone Helmet',
    'Bridgestone T31 Tire',
    'Michelin Pilot Road 5 Tire',
    'EBC Brake Pads',
    'NGK Spark Plug',
    'OHIParts Front Fork Seal Kit',
    'Rinseart Motorcycle Chain Cleaner',
  ];

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (value.length >= 2) {
      setLoading(true);
      debounceRef.current = setTimeout(() => {
        // Local filter of suggestions
        const filtered = placeholderSuggestions.filter(s =>
          s.toLowerCase().includes(value.toLowerCase())
        );
        setResults(filtered.length > 0 ? filtered : placeholderSuggestions.slice(0, 5));
        setLoading(false);
        setShowSuggestions(true);
      }, 300);
    } else {
      setShowSuggestions(false);
      setResults([]);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion);
    setShowSuggestions(false);
    navigate({ pathname: '/shop', search: `search=${encodeURIComponent(suggestion)}` });
    onClose();
  };

  const handleSearch = () => {
    if (query.trim()) {
      navigate({ pathname: '/shop', search: `search=${encodeURIComponent(query)}` });
      onClose();
    }
  };

  return (
    <div ref={searchRef} className="flex flex-col h-full">
      <div className="flex items-center justify-between px-4 pt-4">
        <div className="flex items-center gap-2">
          <Search className="h-5 w-5 text-foreground" />
          <DrawerTitle>Search Products</DrawerTitle>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="px-4 py-4">
        <div className="relative">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="What are you looking for?"
                value={query}
                onChange={handleInputChange}
                onFocus={() => query.length >= 2 && setShowSuggestions(true)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10 pr-10"
              />
              <Button
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 p-0"
                onClick={() => setQuery('')}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <Button onClick={handleSearch} className="bg-foreground text-background hover:bg-muted">
              Go
            </Button>
          </div>

          {/* Suggestions Dropdown */}
          {showSuggestions && results.length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-background border border-border rounded-md shadow-lg max-h-60 overflow-y-auto">
              {loading ? (
                <div className="p-4 space-y-2">
                  {[1, 2, 3].map(i => (
                    <Skeleton key={i} className="h-4 w-full" />
                  ))}
                </div>
              ) : (
                <ul className="py-1">
                  {results.map((suggestion, index) => (
                    <li
                      key={index}
                      className="px-4 py-2 text-sm text-foreground hover:bg-muted cursor-pointer transition-colors"
                      onClick={() => handleSuggestionClick(suggestion)}
                    >
                      {suggestion}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}